import { useEffect, useRef } from 'react';

import type { SignupScreen } from '../CreateAccountFormContext';
import type { EmailVerificationStatus } from './useEmailVerification';
import type { useSignupTracking } from './useSignupTracking';

type UseSignupLifecycleTrackingOptions = {
  screen: SignupScreen;
  isEmailVerified: boolean;
  emailVerificationStatus: EmailVerificationStatus;
  trackStep: ReturnType<typeof useSignupTracking>['trackStep'];
  trackLifecycle: ReturnType<typeof useSignupTracking>['trackLifecycle'];
  getPreviousStepId: ReturnType<typeof useSignupTracking>['getPreviousStepId'];
};

export const useSignupLifecycleTracking = ({
  screen,
  isEmailVerified,
  emailVerificationStatus,
  trackStep,
  trackLifecycle,
  getPreviousStepId,
}: UseSignupLifecycleTrackingOptions) => {
  const hasTrackedWorkspaceSetupRef = useRef(false);
  useEffect(() => {
    if (screen !== 'workspace_setup' || hasTrackedWorkspaceSetupRef.current) {
      return;
    }
    hasTrackedWorkspaceSetupRef.current = true;
    trackStep('impression', {
      step_id: 'workspace_setup',
      step_name: 'Workspace Setup',
      step_index: 0,
      step_count: 1,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  const hasTrackedVerifyEmailRef = useRef(false);
  useEffect(() => {
    if (screen !== 'verify_email' || hasTrackedVerifyEmailRef.current) return;
    hasTrackedVerifyEmailRef.current = true;
    trackStep('impression', {
      step_id: 'verify_email',
      step_name: 'Verify Email',
      step_index: -1,
      step_count: 2,
      navigation: {
        entry_mode: 'direct',
        previous_step_id: getPreviousStepId(),
      },
    });
  }, [screen, trackStep, getPreviousStepId]);

  const verificationTrackingStateRef = useRef<
    'unseen' | 'required' | 'succeeded'
  >('unseen');
  useEffect(() => {
    if (
      !['verify_email', 'login_verify_email'].includes(screen) ||
      verificationTrackingStateRef.current !== 'unseen'
    ) {
      return;
    }
    verificationTrackingStateRef.current = 'required';
    trackLifecycle({
      eventType: 'verification_required',
      trigger: 'impression',
    });
  }, [screen, trackLifecycle]);

  useEffect(() => {
    if (
      verificationTrackingStateRef.current !== 'required' ||
      !isEmailVerified
    ) {
      return;
    }
    verificationTrackingStateRef.current = 'succeeded';
    trackLifecycle({
      eventType: 'verification_succeeded',
      trigger: 'completion',
      outcome: { status: 'success' },
    });
  }, [isEmailVerified, trackLifecycle]);

  useEffect(() => {
    if (emailVerificationStatus === 'pending' && !isEmailVerified) {
      trackLifecycle({
        eventType: 'verification_pending',
        trigger: 'completion',
        outcome: { status: 'pending' },
      });
    }
    if (emailVerificationStatus === 'error') {
      trackLifecycle({
        eventType: 'verification_check_failed',
        trigger: 'error',
        outcome: { status: 'error' },
      });
    }
    if (emailVerificationStatus === 'sign_in_required') {
      trackLifecycle({
        eventType: 'reauthentication_required',
        trigger: 'error',
        outcome: { status: 'required' },
      });
    }
  }, [emailVerificationStatus, isEmailVerified, trackLifecycle]);
};
